import moment from 'moment-timezone';
import React, { useCallback, useState, useEffect } from 'react'
import {
    startOfWeek,
    endOfWeek,
    addDays,
    format,
} from "date-fns";
import ja from "date-fns/locale/ja";
import DatePicker, { registerLocale } from 'react-datepicker';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { TIME_FRAME } from 'utils/constanst';
import PropTypes from 'prop-types';
import "react-datepicker/dist/react-datepicker.css";
import './CustomDatePicker.css';

registerLocale("ja", ja);

export default function CustomDatePicker({ timeUnit, selectedDate, onDateChange }) {
    const [hoveredDate, setHoveredDate] = useState(null);
    const refFocus = React.useRef({
        focusMonth: selectedDate.getMonth(),
        focusYear: selectedDate.getFullYear(),
    });

    useEffect(() => {
        if (selectedDate.getMonth() !== refFocus.current.focusMonth || selectedDate.getFullYear() !== refFocus.current.focusYear) {
            refFocus.current.focusMonth = selectedDate.getMonth();
            refFocus.current.focusYear = selectedDate.getFullYear();
        }
    }, [selectedDate]);

    const adjustDate = useCallback((unit, amount) => {
        const newDate = moment(selectedDate).tz('Asia/Tokyo').add(amount, unit).toDate();
        refFocus.current.focusMonth = newDate.getMonth();
        refFocus.current.focusYear = newDate.getFullYear();
        onDateChange(newDate);
    }, [selectedDate, onDateChange]);

    const handleTimeAdjustment = useCallback((direction) => {
        const adjustmentMap = {
            [TIME_FRAME.DAY]: () => adjustDate('days', direction),
            [TIME_FRAME.TEN_MINUTES]: () => adjustDate('hours', direction),
            [TIME_FRAME.WEEK]: () => adjustDate('weeks', direction),
            [TIME_FRAME.MONTH]: () => adjustDate('months', direction),
            [TIME_FRAME.YEAR]: () => adjustDate('years', direction),
        };
        adjustmentMap[timeUnit]?.();
    }, [timeUnit, adjustDate]);

    const formatWeekNumber = (date) => {
        const targetDate = new Date(date);
        const minus = refFocus.current.focusMonth - targetDate.getMonth();
        if ([1, -11].includes(minus)) return 1;
        if ([-1, 11].includes(minus)) {
            return '';
        }

        const firstDay = new Date(targetDate.getFullYear(), targetDate.getMonth(), 1);
        let firstDayOfWeek = firstDay.getDay();
        firstDayOfWeek = firstDayOfWeek === 0 ? 6 : firstDayOfWeek - 1;
        const dayOfMonth = targetDate.getDate();
        return Math.ceil((dayOfMonth + firstDayOfWeek) / 7);
    };

    const CustomWeekInput = React.forwardRef(({ onClick }, ref) => {
        const startDate = startOfWeek(selectedDate, { weekStartsOn: 1 });
        const endDate = endOfWeek(selectedDate, { weekStartsOn: 1 });
        const value = `${format(startDate, 'MM/dd')} - ${format(endDate, 'MM/dd')}`;

        return (
            <input
                className="custom-datepicker-input"
                onClick={onClick}
                ref={ref}
                value={value}
                readOnly
            />
        );
    });
    CustomWeekInput.displayName = 'CustomWeekInput';

    const selectedDateTimeInTokyo = moment.tz(selectedDate, 'Asia/Tokyo').toDate();
    const selectedDayInTokyo = moment.tz(selectedDate, 'Asia/Tokyo').toDate();
    const weekDates = hoveredDate ? Array.from({ length: 7 }, (_, i) => addDays(startOfWeek(hoveredDate, { weekStartsOn: 1 }), i)) : [];

    const CustomHeader = ({ date, decreaseMonth, increaseMonth, prevMonthButtonDisabled, nextMonthButtonDisabled, withTodayButton = false }) => {

        useEffect(() => {
            if (date.getMonth() !== refFocus.current.focusMonth || date.getFullYear() !== refFocus.current.focusYear) {
                refFocus.current.focusMonth = date.getMonth();
                refFocus.current.focusYear = date.getFullYear();
            }
        }, [date]);

        const nextButtonClassName = `react-datepicker__navigation react-datepicker__navigation--next ${withTodayButton ? 'react-datepicker__navigation--next--with-today-button' : ''}`;

        return (
            <div className="react-datepicker__header">
                <button onClick={decreaseMonth} disabled={prevMonthButtonDisabled} type="button" className="react-datepicker__navigation react-datepicker__navigation--previous" aria-label="Previous Month">
                    <span className="react-datepicker__navigation-icon react-datepicker__navigation-icon--previous">Previous Month</span>
                </button>
                <span className="react-datepicker__current-month" style={{ fontWeight: "bold", fontSize: "0.944rem" }}>
                    {`${date.getFullYear()}年 ${date.getMonth() + 1}月`}
                </span>
                <button onClick={increaseMonth} disabled={nextMonthButtonDisabled} type="button" className={nextButtonClassName} aria-label="Next Month">
                    <span className="react-datepicker__navigation-icon react-datepicker__navigation-icon--next">Next Month</span>
                </button>
            </div>
        );
    };

    const datePickerMap = {
        [TIME_FRAME.TEN_MINUTES]: <DatePicker
            selected={selectedDateTimeInTokyo}
            onChange={onDateChange}
            showTimeSelect
            timeIntervals={60}
            timeCaption="時間"
            dateFormat="yyyy/MM/dd HH:mm"
            locale="ja"
        />,
        [TIME_FRAME.DAY]: <DatePicker
            dateFormat="yyyy/MM/dd"
            selected={selectedDayInTokyo}
            onChange={onDateChange}
            todayButton={"今日"}
            calendarStartDay={1}
            locale="ja"
            renderCustomHeader={(props) => <CustomHeader {...props} />}
        />,
        [TIME_FRAME.WEEK]: <DatePicker
            selected={selectedDayInTokyo}
            onChange={onDateChange}
            showWeekNumbers
            calendarStartDay={1}
            locale="ja"
            customInput={<CustomWeekInput />}
            formatWeekNumber={formatWeekNumber}
            highlightDates={[
                {
                    'react-datepicker__day--week-hover': weekDates,
                },
            ]}
            dayClassName={(date) => {
                const isInWeek =
                    date >= startOfWeek(selectedDate, { weekStartsOn: 1 }) &&
                    date <= endOfWeek(selectedDate, { weekStartsOn: 1 });

                if (isInWeek) return "week-selected";
                return undefined;
            }}
            onDayMouseEnter={(date) => setHoveredDate(date)}
            onCalendarClose={() => setHoveredDate(null)}
            // onMonthChange={onDateChange}
            renderCustomHeader={(props) => <CustomHeader {...props} />}
        />,
        [TIME_FRAME.MONTH]: <DatePicker
            selected={selectedDayInTokyo}
            onChange={onDateChange}
            dateFormat="yyyy/MM"
            showMonthYearPicker
            locale="ja"
        />,
        [TIME_FRAME.YEAR]: <DatePicker
            selected={selectedDayInTokyo}
            onChange={onDateChange}
            dateFormat="yyyy"
            showYearPicker
            locale="ja"
        />,
    }

    return (
        <div className='custom_date_picker_container'>
            <button onClick={() => handleTimeAdjustment(-1)} className="date-picker-arrow-button">
                <FontAwesomeIcon icon={faChevronLeft} />
            </button>
            {datePickerMap[timeUnit]}
            <button onClick={() => handleTimeAdjustment(1)} className="date-picker-arrow-button">
                <FontAwesomeIcon icon={faChevronRight} />
            </button>
        </div>
    );
};

CustomDatePicker.propTypes = {
  timeUnit: PropTypes.string.isRequired,
  selectedDate: PropTypes.instanceOf(Date).isRequired,
  onDateChange: PropTypes.func.isRequired,
};
